import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translateText } from '../services/translate';
import '../styles/AdminDashboard.css';
import '../styles/Partners.css';

const REQUESTS_API = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/partner-requests/`;
const PARTNERS_API = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/partners/`;

export function AdminPartnersContent() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [partnerRequests, setPartnerRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [wasValidated, setWasValidated] = useState(false);
  const [newPartner, setNewPartner] = useState({ nom: '', nom_en: '', nom_ar: '', logo: null });
  const [viewMode, setViewMode] = useState('list');
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState({
    show: false, title: '', message: '', type: 'info', onConfirm: null,
  });

  // ── État du formulaire de modification ──
  const [editPartner, setEditPartner] = useState(null); // { id, nom, nom_en, nom_ar, logo (File|null), logoPreview }
  const [editSubmitting, setEditSubmitting] = useState(false);

  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const canManage = isSuperAdmin || localStorage.getItem('can_manage_partners') === 'true';

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    try {
      const [reqRes, partRes] = await Promise.all([
        fetch(REQUESTS_API, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language || 'fr'
          }
        }),
        fetch(PARTNERS_API, {
          headers: {
            'Accept-Language': 'fr'
          }
        }),
      ]);

      if (reqRes.status === 401 || reqRes.status === 403) {
        localStorage.removeItem('access_token');
        navigate('/admin/login');
        return;
      }

      if (reqRes.ok) {
        const data = await reqRes.json();
        setPartnerRequests(Array.isArray(data) ? data : (data.results || []));
      } else {
        setError(`Erreur ${reqRes.status} lors de la récupération des demandes.`);
      }

      if (partRes.ok) {
        const data = await partRes.json();
        setPartners(Array.isArray(data) ? data : (data.results || []));
      } else {
        setError(`Erreur ${partRes.status} : Impossible de charger les partenaires.`);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("Erreur de connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]); // Re-charger si la langue change

  const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
  const notify = (type, title, message) => setModal({ show: true, type, title, message, onConfirm: null });
  const confirm = (title, message, onConfirm) =>
    setModal({ show: true, type: 'warning', title, message, onConfirm });

  const handleViewRequest = (req) => {
    notify('info', `Demande de ${req.nom}`,
      `Organisation : ${req.organisation || 'N/A'}\nEmail : ${req.email}\nTél : ${req.tel || 'N/A'}\n\nMessage :\n${req.message}`);
  };

  const handleDeleteRequest = (id) => {
    confirm('Supprimer la demande', 'Voulez-vous vraiment supprimer cette demande ?', async () => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${REQUESTS_API}${id}/`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { fetchData(); notify('success', 'Supprimé', 'La demande a été supprimée.'); }
      else notify('error', 'Erreur', `Erreur ${res.status} : Impossible de supprimer.`);
    });
  };

  const handleDeletePartner = (id) => {
    confirm('Retirer le partenaire', 'Ce partenaire ne sera plus affiché. Confirmer ?', async () => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${PARTNERS_API}${id}/`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { fetchData(); notify('success', 'Retiré', 'Le partenaire a été retiré.'); }
      else notify('error', 'Erreur', `Erreur ${res.status} : Impossible de supprimer.`);
    });
  };

  const handleAutoTranslate = async () => {
    if (!newPartner.nom) {
      notify('warning', 'Champ vide', 'Veuillez remplir le nom du partenaire en français.');
      return;
    }
    setSubmitting(true);
    try {
      const en = await translateText(newPartner.nom, 'fr', 'en');
      const ar = await translateText(newPartner.nom, 'fr', 'ar');
      setNewPartner(prev => ({ ...prev, nom_en: en, nom_ar: ar }));
      notify('success', 'Traduction terminée', 'Le nom a été traduit en Anglais et Arabe.');
    } catch {
      notify('error', 'Erreur', 'La traduction a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    setWasValidated(true);
    if (!newPartner.logo || !newPartner.nom) {
      if (!newPartner.logo) notify('error', 'Champ manquant', 'Veuillez sélectionner un logo.');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append('nom', newPartner.nom);
    fd.append('nom_en', newPartner.nom_en);
    fd.append('nom_ar', newPartner.nom_ar);
    fd.append('logo', newPartner.logo);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(PARTNERS_API, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) {
        setShowAddForm(false);
        setNewPartner({ nom: '', nom_en: '', nom_ar: '', logo: null });
        fetchData();
        notify('success', 'Partenaire ajouté', 'Le nouveau partenaire a été enregistré.');
      } else notify('error', 'Erreur', `Erreur ${res.status} : Impossible d'ajouter le partenaire.`);
    } catch { notify('error', 'Erreur serveur', 'Connexion au serveur impossible.'); }
    finally { setSubmitting(false); }
  };

  // ── Ouvrir le formulaire de modification ──
  const handleOpenEdit = (p) => {
    setEditPartner({ id: p.id, nom: p.nom || '', nom_en: p.nom_en || '', nom_ar: p.nom_ar || '', logo: null, logoPreview: p.logo || null });
  };

  const handleEditAutoTranslate = async () => {
    if (!editPartner.nom) { notify('warning', 'Champ vide', 'Veuillez remplir le nom en français.'); return; }
    setEditSubmitting(true);
    try {
      const en = await translateText(editPartner.nom, 'fr', 'en');
      const ar = await translateText(editPartner.nom, 'fr', 'ar');
      setEditPartner(prev => ({ ...prev, nom_en: en, nom_ar: ar }));
      notify('success', 'Traduction terminée', 'Le nom a été traduit en Anglais et Arabe.');
    } catch { notify('error', 'Erreur', 'La traduction a échoué.'); }
    finally { setEditSubmitting(false); }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editPartner.nom.trim()) { notify('error', 'Champ manquant', 'Le nom en français est obligatoire.'); return; }
    setEditSubmitting(true);
    const fd = new FormData();
    fd.append('nom', editPartner.nom);
    fd.append('nom_en', editPartner.nom_en);
    fd.append('nom_ar', editPartner.nom_ar);
    if (editPartner.logo) fd.append('logo', editPartner.logo);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${PARTNERS_API}${editPartner.id}/`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) {
        setEditPartner(null);
        fetchData();
        notify('success', 'Modifié', 'Le partenaire a été mis à jour.');
      } else notify('error', 'Erreur', `Erreur ${res.status} : Impossible de modifier.`);
    } catch { notify('error', 'Erreur serveur', 'Connexion au serveur impossible.'); }
    finally { setEditSubmitting(false); }
  };

  const IconEye = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
  const IconTrash = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );

  const IconEdit = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );

  return (
    <>
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
            <div className={`tiwizi-modal-icon ${modal.type === 'success' ? 'success' : modal.type === 'warning' ? 'warning' : 'error'}`}>
              {modal.type === 'success'
                ? <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              }
            </div>
            <h3>{modal.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              {modal.onConfirm ? (
                <>
                  <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
                  <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
                </>
              ) : (
                <button className="tiwizi-btn-cancel" onClick={closeModal}>Fermer</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de modification ── */}
      {editPartner && (
        <div className="tiwizi-modal-overlay" onClick={() => setEditPartner(null)}>
          <div className="tiwizi-modal-card" style={{ maxWidth: '600px', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px' }}>Modifier le partenaire</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-grid" style={{ marginBottom: '16px' }}>
                <div className="form-column">
                  <div className="form-field">
                    <label>Nom (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input type="text" value={editPartner.nom} onChange={e => setEditPartner(p => ({ ...p, nom: e.target.value }))} placeholder="Nom en français" required />
                  </div>
                  <div className="form-field">
                    <label>Nom (EN)</label>
                    <input type="text" value={editPartner.nom_en} onChange={e => setEditPartner(p => ({ ...p, nom_en: e.target.value }))} placeholder="Name in English" />
                  </div>
                  <div className="form-field">
                    <label>Nom (AR)</label>
                    <input type="text" value={editPartner.nom_ar} onChange={e => setEditPartner(p => ({ ...p, nom_ar: e.target.value }))} placeholder="الاسم بالعربية" style={{ direction: 'rtl' }} />
                  </div>
                  <button
                    type="button"
                    onClick={handleEditAutoTranslate}
                    disabled={editSubmitting}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: '#f5c50a', color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.82rem', marginTop: '4px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    {editSubmitting ? 'Traduction...' : 'Traduire automatiquement'}
                  </button>
                </div>
                <div className="form-column">
                  <div className="form-field">
                    <label>Logo <span style={{ color: '#888', fontWeight: 400 }}>(laisser vide pour conserver)</span></label>
                    <label className={`upload-zone ${editPartner.logo || editPartner.logoPreview ? 'has-file' : ''}`} style={{ minHeight: '130px' }}>
                      {editPartner.logo ? (
                        <><img src={URL.createObjectURL(editPartner.logo)} alt="Nouveau logo" /><span>{editPartner.logo.name}</span></>
                      ) : editPartner.logoPreview ? (
                        <><img src={editPartner.logoPreview} alt="Logo actuel" /><span style={{ fontSize: '0.75rem', color: '#888' }}>Logo actuel — cliquer pour changer</span></>
                      ) : (
                        <><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f5c50a" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg><span className="upload-text">Cliquer pour choisir</span></>
                      )}
                      <input type="file" accept="image/*" onChange={e => setEditPartner(p => ({ ...p, logo: e.target.files[0] }))} />
                    </label>
                  </div>
                </div>
              </div>
              <div className="tiwizi-modal-actions" style={{ marginTop: '10px' }}>
                <button type="button" className="tiwizi-btn-cancel" onClick={() => setEditPartner(null)}>Annuler</button>
                <button type="submit" className="btn-submit-partner" style={{ width: 'auto', padding: '12px 28px' }} disabled={editSubmitting}>
                  {editSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="data-section" style={{ marginBottom: '30px' }}>
        <div className="section-title-group">
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
            Demandes de partenariat
            <span className="count-badge">{partnerRequests.length}</span>
          </h3>
        </div>

        <div className="admin-cards-container">
          {loading ? <p className="loading-text">Chargement...</p> : error ? (
            <p className="error-text" style={{ textAlign: 'center', color: '#e74c3c' }}>{error}</p>
          ) : (
            <div className="admin-requests-grid">
              {partnerRequests.length === 0 ? (
                <p className="empty-table" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#999', fontStyle: 'italic' }}>
                  Aucune demande pour le moment.
                </p>
              ) : partnerRequests.map(req => (
                <div className="admin-request-card" key={req.id}>
                  <div className="request-card-header">
                    <span className="request-date">{new Date(req.created_at).toLocaleDateString('fr-FR')}</span>
                    <div className="action-btns">
                      <button className="icon-btn" title="Voir les détails" onClick={() => handleViewRequest(req)}><IconEye /></button>
                      {canManage && (
                        <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDeleteRequest(req.id)}><IconTrash /></button>
                      )}
                    </div>
                  </div>
                  <div className="request-card-body">
                    <h4>{req.nom}</h4>
                    <p className="request-org">{req.organisation || 'Individuel'}</p>
                    <p className="request-email">{req.email}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="data-section">
        <div className="section-title-group">
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
            Nos Partenaires
            <span className="count-badge dark">{partners.length}</span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="projets-view-toggle">
              <button type="button" className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vue Liste">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
              <button type="button" className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vue Grille">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
            </div>
            {canManage && (
              <button onClick={() => { setShowAddForm(!showAddForm); setWasValidated(false); }} className="btn-add-partner">
                {showAddForm ? 'Annuler' : '+ Ajouter un partenaire'}
              </button>
            )}
          </div>
        </div>

        {showAddForm && (
          <div className="add-partner-form-container">
            <h4 style={{ marginTop: 0, marginBottom: '20px' }}>Nouveau partenaire</h4>
            <form onSubmit={handleAddPartner} className={`add-partner-form ${wasValidated ? 'was-validated' : ''}`} noValidate>
              <div className="form-grid">
                <div className="form-column">
                  <div className="form-field">
                    <label>Nom du partenaire (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input type="text" placeholder="Ex: Association Azrou" value={newPartner.nom} onChange={e => setNewPartner({ ...newPartner, nom: e.target.value })} required />
                  </div>
                  <div className="form-field">
                    <label>Nom du partenaire (EN)</label>
                    <input type="text" placeholder="Ex: Azrou Association" value={newPartner.nom_en} onChange={e => setNewPartner({ ...newPartner, nom_en: e.target.value })} />
                  </div>
                  <div className="form-field">
                    <label>Nom du partenaire (AR)</label>
                    <input type="text" placeholder="..." value={newPartner.nom_ar} onChange={e => setNewPartner({ ...newPartner, nom_ar: e.target.value })} style={{ direction: 'rtl' }} />
                  </div>
                </div>
                <div className="form-column">
                  <div className="form-field">
                    <label>Logo du partenaire <span style={{ color: '#e74c3c' }}>*</span></label>
                    <label className={`upload-zone ${newPartner.logo ? 'has-file' : ''} ${(wasValidated && !newPartner.logo) ? 'invalid' : ''}`}>
                      {newPartner.logo ? (
                        <>
                          <img src={URL.createObjectURL(newPartner.logo)} alt="Prévisu" />
                          <span>{newPartner.logo.name}</span>
                        </>
                      ) : (
                        <>
                          <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#f5c50a" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                          <span className="upload-text">Cliquer pour choisir le logo</span>
                        </>
                      )}
                      <input type="file" accept="image/*" onChange={e => setNewPartner({ ...newPartner, logo: e.target.files[0] })} />
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Outils de traduction</span>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    className="btn-translate-auto"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#f5c50a',
                      color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem'
                    }}
                    disabled={submitting}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    {submitting ? 'Traduction...' : 'Traduire automatiquement'}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  Remplit le nom en Anglais et en Arabe à partir du Français.
                </p>
              </div>

              <button type="submit" className="btn-submit-partner" disabled={submitting}>
                {submitting ? 'Enregistrement...' : 'Enregistrer le partenaire'}
              </button>
            </form>
          </div>
        )}

        <div className="admin-table-container">
          {loading ? <p className="loading-text">Chargement...</p> : (
            <>
              {viewMode === 'list' && (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center' }}>Logo</th>
                      <th style={{ textAlign: 'center' }}>Nom</th>
                      <th style={{ textAlign: 'center' }}>Date</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.length === 0 ? (
                      <tr><td colSpan="4" className="empty-table">Aucun partenaire enregistré.</td></tr>
                    ) : partners.map(p => (
                      <tr key={p.id}>
                        <td>{p.logo ? <img src={p.logo} alt={p.nom} className="table-logo-preview" /> : '—'}</td>
                        <td style={{ textAlign: 'left' }}><strong>{p.nom_fr || p.nom}</strong></td>
                        <td>{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                        <td>
                          {canManage ? (
                            <div className="action-btns" style={{ justifyContent: 'center' }}>
                              <button className="icon-btn" title="Modifier" style={{ color: '#f5c50a' }} onClick={() => handleOpenEdit(p)}><IconEdit /></button>
                              <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDeletePartner(p.id)}><IconTrash /></button>
                            </div>
                          ) : (
                            <span title="Accès restreint" style={{ color: '#cbd5e1', display: 'inline-flex' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {viewMode === 'grid' && (
                partners.length === 0 ? (
                  <p className="empty-table">Aucun partenaire enregistré.</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '20px', marginTop: '10px' }}>
                    {partners.map(p => (
                      <div key={p.id} style={{ background: 'white', borderRadius: '20px', padding: '24px 16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid #eee' }}>
                          {p.logo
                            ? <img src={p.logo} alt={p.nom} style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                            : <span style={{ fontSize: '1.5rem', fontWeight: '800', color: '#f5c50a' }}>{p.nom?.[0]}</span>
                          }
                        </div>
                        <strong style={{ fontSize: '0.9rem', color: '#1a1a1a' }}>{p.nom}</strong>
                        <span style={{ fontSize: '0.75rem', color: '#bbb' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</span>
                        {canManage && (
                          <div className="action-btns">
                            <button className="icon-btn" title="Modifier" style={{ color: '#f5c50a' }} onClick={() => handleOpenEdit(p)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                            </button>
                            <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDeletePartner(p.id)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

function AdminPartners() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('access_token')) navigate('/admin/login');
  }, [navigate]);

  return (
    <div className="admin-layout">
      <main className="admin-main" style={{ marginLeft: 0 }}>
        <div className="admin-content">
          <AdminPartnersContent />
        </div>
      </main>
    </div>
  );
}

export default AdminPartners;
